import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { camelCaseTransformer } from '@sondermind/utilities/tools';

import {
  IAmplitudeFlagsResponse,
  IAssignVariantResponse,
  IClearAssignmentResponse,
} from '../models/amplitude-flag-http.model';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeFlagHttpService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    private config: ConfigurationService
  ) {
    this.baseUrl = this.config.env.apiBase || '';
  }

  /**
   * Fetch all configured flags with current evaluations for authenticated/anonymous user
   * Auth handled via Bearer token (injected by Angular interceptors) or X-Amplitude-Anonymous-Id header
   */
  fetchFlags$(anonymousId?: string): Observable<IAmplitudeFlagsResponse> {
    const headers = this.buildHeaders(anonymousId);

    return this.http.get(
      `${this.baseUrl}/v2/amplitude_flags`,
      { headers }
    ).pipe(
      map((response) => camelCaseTransformer<IAmplitudeFlagsResponse>(response))
    );
  }

  /**
   * Assign the authenticated/anonymous user to a specific variant
   */
  assignVariant$(
    flagKey: string,
    variant: 'on' | 'off',
    anonymousId?: string
  ): Observable<IAssignVariantResponse> {
    const headers = this.buildHeaders(anonymousId);
    const body = { variant };

    return this.http.put(
      `${this.baseUrl}/v2/amplitude_flags/${flagKey}`,
      body,
      { headers }
    ).pipe(
      map((response) => camelCaseTransformer<IAssignVariantResponse>(response))
    );
  }

  /**
   * Clear variant assignment (remove override)
   */
  clearAssignment$(
    flagKey: string,
    anonymousId?: string
  ): Observable<IClearAssignmentResponse> {
    const headers = this.buildHeaders(anonymousId);

    return this.http.delete(
      `${this.baseUrl}/v2/amplitude_flags/${flagKey}`,
      { headers }
    ).pipe(
      map((response) => camelCaseTransformer<IClearAssignmentResponse>(response))
    );
  }

  /**
   * Build headers with optional anonymous ID
   * If user is authenticated, Angular HTTP interceptors will add Bearer token automatically
   * If anonymous, we add the X-Amplitude-Anonymous-Id header
   */
  private buildHeaders(anonymousId?: string): HttpHeaders {
    let headers = new HttpHeaders();

    if (anonymousId) {
      headers = headers.set('X-Amplitude-Anonymous-Id', anonymousId);
    }

    return headers;
  }
}
